/**
 * Disable Account Step Component
 * Uses centralized validation service for consistency
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import useFormValidation from '../../hooks/useFormValidation';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import StepValidationService from '../../services/stepValidationService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

interface DisableAccountStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
  onSkip: (currentStep: string, nextStep: string) => void;
}

/**
 * DisableAccountStep component for handling account disable configuration
 */
function DisableAccountStep({
  message,
  onFormSubmit,
  onSkip,
}: DisableAccountStepProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Get persisted form data from Redux
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.DisableAccount
  );

  // Check if in edit mode
  const isInEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  // Check if step is completed
  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const skippedSteps = useSelector(
    (state: RootState) => state.copilot.navigation.skippedSteps ?? []
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');
  const isStepCompletedOrSkipped =
    isCompletedCheck.includes('DisableAccount') ||
    skippedSteps.includes('DisableAccount');

  const { validateForm } = useFormValidation();

  const validateAndContinue = useCallback(async () => {
    setValidationError(null);

    // Use centralized validation service
    const validation = await StepValidationService.validateStep(
      'DisableAccount',
      persistedFormData,
      validateForm
    );

    if (validation.isValid) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: 'DisableAccount',
          formData: persistedFormData,
        })
      );

      onFormSubmit('DisableAccount', persistedFormData, validation.isValid);
    } else {
      // Show validation error
      setValidationError(validation.errors[0] || 'Validation failed');
    }

    // In edit mode, don't collapse form after successful submission
    return validation.isValid && !isInEditMode;
  }, [persistedFormData, validateForm, isInEditMode, dispatch, onFormSubmit]);

  const handleSkipStep = () => {
    onSkip('DisableAccount', 'RemoveAccount');
  };

  return (
    <ResponseWithForm
      responseText={
        !isInEditMode
          ? FORM_RESPONSE_TEXTS.DISABLE_ACCOUNT
          : FORM_RESPONSE_TEXTS.DISABLE_ACCOUNT_EDIT
      }>
      <ExpandableForm
        stepName={message.stepName ?? 'DisableAccount'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}
        onSecondory={handleSkipStep}
        secondryButtonLabel={isInEditMode ? undefined : 'Skip'}
        isSecondaryDisabled={isStepCompletedOrSkipped}>
        <StepRenderer stepName='DisableAccount' />
      </ExpandableForm>
      {validationError && <ErrorBanner message={validationError} />}
    </ResponseWithForm>
  );
}

export default DisableAccountStep;
